/**
 * Custom Quick Add for Collection Pages
 * Standalone implementation - works independently from theme's quick-add
 */

(function() {
  'use strict';

  // Configuration
  const CONFIG = {
    modalId: 'custom-quick-add-modal',
    modalContentId: 'custom-quick-add-modal-content',
    modalOverlayId: 'custom-quick-add-modal-overlay',
    modalCloseId: 'custom-quick-add-modal-close',
    buttonClass: 'custom-quick-add-btn',
    chooseOptionsClass: 'custom-choose-options-btn',
    addToCartClass: 'custom-direct-add-to-cart-btn'
  };

  /**
   * Initialize the custom quick-add system
   */
  function init() {
    createModal();
    attachEventListeners();
    console.log('Custom Quick Add: Initialized');
  }

  /**
   * Create the modal HTML structure
   */
  function createModal() {
    if (document.getElementById(CONFIG.modalId)) {
      return; // Modal already exists
    }

    const modalHTML = `
      <div id="${CONFIG.modalId}" class="custom-quick-add-modal" style="display: none;">
        <div id="${CONFIG.modalOverlayId}" class="custom-quick-add-modal__overlay"></div>
        <div class="custom-quick-add-modal__container">
          <button id="${CONFIG.modalCloseId}" class="custom-quick-add-modal__close" aria-label="Close">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
            </svg>
          </button>
          <div id="${CONFIG.modalContentId}" class="custom-quick-add-modal__content">
            <div class="custom-quick-add-modal__loading">Loading...</div>
          </div>
        </div>
      </div>
    `;

    document.body.insertAdjacentHTML('beforeend', modalHTML);
  }

  /**
   * Attach event listeners
   */
  function attachEventListeners() {
    // Use event delegation for buttons
    document.addEventListener('click', function(e) {
      // Handle "Choose Options" button
      if (e.target.closest(`.${CONFIG.chooseOptionsClass}`)) {
        e.preventDefault();
        e.stopPropagation();
        const button = e.target.closest(`.${CONFIG.chooseOptionsClass}`);
        const productId = button.dataset.productId;
        const productHandle = button.dataset.productHandle;
        const productUrl = button.dataset.productUrl;
        
        if (productId && productHandle) {
          openModal(productId, productHandle, productUrl);
        }
      }

      // Handle direct "Add to Cart" button
      if (e.target.closest(`.${CONFIG.addToCartClass}`)) {
        e.preventDefault();
        e.stopPropagation();
        const button = e.target.closest(`.${CONFIG.addToCartClass}`);
        const variantId = button.dataset.variantId;
        
        if (variantId) {
          addToCartDirect(variantId, button);
        }
      }

      // Handle modal close
      if (e.target.id === CONFIG.modalCloseId || e.target.closest(`#${CONFIG.modalCloseId}`) || 
          e.target.id === CONFIG.modalOverlayId || e.target.closest(`#${CONFIG.modalOverlayId}`)) {
        closeModal();
      }
    });

    // Close modal on Escape key
    document.addEventListener('keydown', function(e) {
      if (e.key === 'Escape') {
        const modal = document.getElementById(CONFIG.modalId);
        if (modal && modal.style.display !== 'none') {
          closeModal();
        }
      }
    });

    // Handle add to cart from modal
    document.addEventListener('click', function(e) {
      if (e.target.closest('.custom-modal-add-to-cart-btn')) {
        e.preventDefault();
        const button = e.target.closest('.custom-modal-add-to-cart-btn');
        const variantId = button.dataset.variantId;
        const quantity = parseInt(button.dataset.quantity || 1);
        
        if (variantId) {
          const originalText = button.innerHTML;
          button.disabled = true;
          button.innerHTML = 'Adding...';
          
          // Try to use theme's cart.addItemById function if available
          if (window.theme && window.theme.cart && typeof window.theme.cart.addItemById === 'function') {
            window.theme.cart.addItemById(parseInt(variantId), quantity)
              .then((result) => {
              button.innerHTML = 'Added!';
              button.classList.add('added');
              
              // Close modal first
              closeModal();
              
              // Theme's addItemById already triggers cart:updated and quick-cart:updated
              // This updates counter and drawer content automatically
              // We don't open the drawer (matching default theme behavior)
              })
              .catch(error => {
                console.error('Error adding to cart:', error);
                button.innerHTML = 'Error - Try Again';
                button.disabled = false;
                
                setTimeout(() => {
                  button.innerHTML = originalText;
                }, 2000);
              });
          } else {
            // Fallback: use direct API call (same format as theme's addItemById)
            const cartAddUrl = '/cart/add.js';
            
            fetch(cartAddUrl, {
              method: 'POST',
              credentials: 'include',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                items: [{
                  id: parseInt(variantId),
                  quantity: quantity
                }]
              })
            })
            .then(response => {
              // Check if response is OK
              if (!response.ok) {
                return response.text().then(text => {
                  throw new Error(`HTTP ${response.status}: ${text.substring(0, 200)}`);
                });
              }
              
              // Check if response is JSON
              const contentType = response.headers.get('content-type');
              if (contentType && contentType.includes('application/json')) {
                return response.json();
              }
              
              // If not JSON, try to parse anyway
              return response.text().then(text => {
                try {
                  return JSON.parse(text);
                } catch (e) {
                  throw new Error(`Expected JSON but got: ${text.substring(0, 200)}`);
                }
              });
            })
            .then(res => {
              // Check for errors
              if (res.status === 422) {
                throw new Error(res.description || 'Failed to add item to cart');
              }
              
              // Fetch updated cart
              return fetch('/cart.js')
                .then(response => response.json())
                .then(cart => {
                  button.innerHTML = 'Added!';
                  button.classList.add('added');
                  
                  // Close modal first
                  closeModal();
                  
                  // Trigger events exactly like theme does
                  document.dispatchEvent(new CustomEvent('quick-cart:updated', { bubbles: true }));
                  document.dispatchEvent(new CustomEvent('cart:updated', {
                    detail: { cart: cart },
                    bubbles: true
                  }));
                  
                  // Also manually refresh drawer
                  refreshCartDrawerContent().then(() => {
                    updateHeaderCartCounterDirect(cart);
                  }).catch(() => {
                    updateHeaderCartCounterDirect(cart);
                  });
                  
                  // Reset button after 1.5 seconds
                  setTimeout(() => {
                    button.innerHTML = originalText;
                    button.disabled = false;
                    button.classList.remove('added');
                  }, 1500);
                });
            })
            .catch(error => {
              console.error('Error adding to cart:', error);
              button.innerHTML = 'Error - Try Again';
              button.disabled = false;
              
              setTimeout(() => {
                button.innerHTML = originalText;
              }, 2000);
            });
          }
        }
      }
    });

    // Handle quantity changes in modal
    document.addEventListener('click', function(e) {
      if (e.target.closest('.custom-quantity-minus')) {
        e.preventDefault();
        const minusBtn = e.target.closest('.custom-quantity-minus');
        const input = minusBtn.nextElementSibling;
        if (input && input.tagName === 'INPUT') {
          let value = parseInt(input.value) || 1;
          if (value > 1) {
            input.value = value - 1;
            updateQuantityInModal(input);
          }
        }
      }

      if (e.target.closest('.custom-quantity-plus')) {
        e.preventDefault();
        const plusBtn = e.target.closest('.custom-quantity-plus');
        const input = plusBtn.previousElementSibling;
        if (input && input.tagName === 'INPUT') {
          let value = parseInt(input.value) || 1;
          input.value = value + 1;
          updateQuantityInModal(input);
        }
      }
    });
  }

  /**
   * Open modal and load product variants
   */
  function openModal(productId, productHandle, productUrl) {
    const modal = document.getElementById(CONFIG.modalId);
    const modalContent = document.getElementById(CONFIG.modalContentId);
    
    if (!modal || !modalContent) return;

    // Show loading state
    modalContent.innerHTML = '<div class="custom-quick-add-modal__loading">Loading options...</div>';
    modal.style.display = 'flex';
    document.body.style.overflow = 'hidden';

    // Fetch product data from Shopify JSON API
    fetch(`/products/${productHandle}.js`)
      .then(response => response.json())
      .then(product => {
        renderModalContent(product, productUrl);
      })
      .catch(error => {
        console.error('Error fetching product:', error);
        modalContent.innerHTML = `
          <div class="custom-quick-add-modal__error">
            <p>Failed to load product options. Please try again.</p>
            <button onclick="this.closest('.custom-quick-add-modal').style.display='none'; document.body.style.overflow='';" class="btn">Close</button>
          </div>
        `;
      });
  }

  /**
   * Render product variants in modal
   */
  function renderModalContent(product, productUrl) {
    const modalContent = document.getElementById(CONFIG.modalContentId);
    if (!modalContent) return;

    let html = `
      <div class="custom-quick-add-modal__header">
        <h2 class="custom-quick-add-modal__title">${escapeHtml(product.title)}</h2>
      </div>
      <div class="custom-quick-add-modal__body">
    `;

    // If product has variants, show variant selector
    if (product.variants && product.variants.length > 1) {
      html += '<div class="custom-quick-add-modal__variants">';
      
      // Group variants by options
      const options = product.options || [];
      options.forEach((option, optionIndex) => {
        if (option.values && option.values.length > 0) {
          html += `
            <div class="custom-variant-option-group">
              <label class="custom-variant-option-label">${escapeHtml(option.name)}:</label>
              <div class="custom-variant-option-values" data-option-index="${optionIndex}">
          `;
          
          option.values.forEach(value => {
            html += `
              <button 
                type="button" 
                class="custom-variant-option-value" 
                data-option-name="${escapeHtml(option.name)}"
                data-option-value="${escapeHtml(value)}"
                data-option-index="${optionIndex}"
              >
                ${escapeHtml(value)}
              </button>
            `;
          });
          
          html += '</div></div>';
        }
      });

      html += '</div>';

      // Variant selection display
      html += `
        <div class="custom-selected-variant" style="display: none;">
          <div class="custom-variant-info">
            <span class="custom-variant-title"></span>
            <span class="custom-variant-price"></span>
            <span class="custom-variant-availability"></span>
          </div>
          <div class="custom-variant-quantity">
            <button type="button" class="custom-quantity-minus">-</button>
            <input type="number" class="custom-quantity-input" value="1" min="1" data-variant-id="">
            <button type="button" class="custom-quantity-plus">+</button>
          </div>
          <button type="button" class="custom-modal-add-to-cart-btn btn btn--primary" data-variant-id="" disabled>
            Add to Cart
          </button>
        </div>
      `;
    } else {
      // Single variant product
      const variant = product.variants[0];
      const price = formatMoney(variant.price);
      const comparePrice = variant.compare_at_price && variant.compare_at_price > variant.price 
        ? formatMoney(variant.compare_at_price) 
        : null;
      
      html += `
        <div class="custom-single-variant">
          <div class="custom-variant-price">
            ${comparePrice ? `
              <span class="visually-hidden">Regular price</span>
              <span class="custom-price sale">${price}</span>
              <s class="custom-compare-price t-subdued">${comparePrice}</s>
            ` : `
              <span class="custom-price">${price}</span>
            `}
          </div>
          <div class="custom-variant-quantity">
            <button type="button" class="custom-quantity-minus">-</button>
            <input type="number" class="custom-quantity-input" value="1" min="1" data-variant-id="${variant.id}">
            <button type="button" class="custom-quantity-plus">+</button>
          </div>
          <button type="button" class="custom-modal-add-to-cart-btn btn btn--primary" data-variant-id="${variant.id}">
            Add to Cart
          </button>
        </div>
      `;
    }

    html += '</div></div>';

    modalContent.innerHTML = html;

    // Initialize variant selection if multiple variants
    if (product.variants && product.variants.length > 1) {
      initVariantSelection(product);
    }
  }

  /**
   * Initialize variant selection logic
   */
  function initVariantSelection(product) {
    const optionValues = document.querySelectorAll('.custom-variant-option-value');
    const selectedVariantDiv = document.querySelector('.custom-selected-variant');
    const variantInfo = {
      options: {}
    };

    optionValues.forEach(btn => {
      btn.addEventListener('click', function() {
        // Remove active class from siblings
        const siblings = this.parentElement.querySelectorAll('.custom-variant-option-value');
        siblings.forEach(s => s.classList.remove('active'));
        
        // Add active to clicked button
        this.classList.add('active');
        
        // Store selected option
        const optionIndex = parseInt(this.dataset.optionIndex);
        variantInfo.options[optionIndex] = this.dataset.optionValue;
        
        // Find matching variant
        const selectedVariant = findVariant(product, variantInfo.options);
        
        if (selectedVariant) {
          updateSelectedVariant(selectedVariant, product);
        }
      });
    });
  }

  /**
   * Find variant based on selected options
   */
  function findVariant(product, selectedOptions) {
    // Check if all options are selected
    const optionCount = product.options ? product.options.length : 0;
    const selectedCount = Object.keys(selectedOptions).length;
    
    if (selectedCount < optionCount) {
      return null; // Not all options selected yet
    }

    return product.variants.find(variant => {
      return variant.options.every((option, index) => {
        const selected = selectedOptions[index];
        // Case-insensitive comparison
        return selected && selected.toString().toLowerCase() === option.toString().toLowerCase();
      });
    });
  }

  /**
   * Update selected variant display
   */
  function updateSelectedVariant(variant, product) {
    const selectedVariantDiv = document.querySelector('.custom-selected-variant');
    const variantTitle = selectedVariantDiv.querySelector('.custom-variant-title');
    const variantPrice = selectedVariantDiv.querySelector('.custom-variant-price');
    const variantAvailability = selectedVariantDiv.querySelector('.custom-variant-availability');
    const quantityInput = selectedVariantDiv.querySelector('.custom-quantity-input');
    const addToCartBtn = selectedVariantDiv.querySelector('.custom-modal-add-to-cart-btn');

    if (!selectedVariantDiv) return;

    selectedVariantDiv.style.display = 'block';

    // Update variant info
    variantTitle.textContent = variant.title || product.title;
    
    // Format price with compare price if available
    const price = formatMoney(variant.price);
    const comparePrice = variant.compare_at_price && variant.compare_at_price > variant.price 
      ? formatMoney(variant.compare_at_price) 
      : null;
    
    if (comparePrice) {
      variantPrice.innerHTML = `
        <span class="visually-hidden">Regular price</span>
        <span class="custom-price sale">${price}</span>
        <s class="custom-compare-price t-subdued">${comparePrice}</s>
      `;
    } else {
      variantPrice.innerHTML = `<span class="custom-price">${price}</span>`;
    }
    
    if (variant.available) {
      variantAvailability.textContent = 'In stock';
      variantAvailability.className = 'custom-variant-availability in-stock';
      addToCartBtn.disabled = false;
    } else {
      variantAvailability.textContent = 'Out of stock';
      variantAvailability.className = 'custom-variant-availability out-of-stock';
      addToCartBtn.disabled = true;
    }

    // Update quantity input and button
    quantityInput.dataset.variantId = variant.id;
    addToCartBtn.dataset.variantId = variant.id;
  }

  /**
   * Update quantity in modal
   */
  function updateQuantityInModal(input) {
    const variantId = input.dataset.variantId;
    const quantity = parseInt(input.value) || 1;
    const addToCartBtn = input.closest('.custom-selected-variant, .custom-single-variant')
      ?.querySelector('.custom-modal-add-to-cart-btn');
    
    if (addToCartBtn && variantId) {
      addToCartBtn.dataset.quantity = quantity;
    }
  }

  /**
   * Add to cart directly (no modal)
   * Uses theme's cart system if available, otherwise falls back to direct API call
   */
  function addToCartDirect(variantId, button, quantity = 1) {
    if (!variantId) return;

    const originalText = button.innerHTML;
    button.disabled = true;
    button.innerHTML = 'Adding...';

    // Try to use theme's cart.addItemById if available
    if (window.theme && window.theme.cart && typeof window.theme.cart.addItemById === 'function') {
      window.theme.cart.addItemById(parseInt(variantId), quantity)
        .then((result) => {
          button.innerHTML = 'Added!';
          button.classList.add('added');
          
          // Theme's addItemById already triggers cart:updated and quick-cart:updated
          // Also manually refresh drawer to ensure it updates
          refreshCartDrawerContent().then(() => {
            if (result && result.cart) {
              updateHeaderCartCounterDirect(result.cart);
            }
          }).catch(() => {
            if (result && result.cart) {
              updateHeaderCartCounterDirect(result.cart);
            }
          });
          
          // Reset button after 1.5 seconds
          setTimeout(() => {
            button.innerHTML = originalText;
            button.disabled = false;
            button.classList.remove('added');
          }, 1500);
        })
        .catch(error => {
          console.error('Error adding to cart via theme:', error);
          // Fall through to direct API call below
        });
      
      if (window.theme && window.theme.cart && typeof window.theme.cart.addItemById === 'function') {
        // If theme method was called, don't proceed with direct API call
        return;
      }
    }

    // Direct API call (if theme method not available or failed)
    const cartAddUrl = '/cart/add.js';
    
    // Try new format first (items array)
    fetch(cartAddUrl, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        items: [{
          id: parseInt(variantId),
          quantity: quantity
        }]
      })
    })
    .then(response => {
      // Check if response is OK
      if (!response.ok) {
        return response.text().then(text => {
          throw new Error(`HTTP ${response.status}: ${text.substring(0, 200)}`);
        });
      }
      
      // Check if response is JSON
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        return response.json();
      }
      
      // If not JSON, try to parse anyway (some responses might not have content-type header)
      return response.text().then(text => {
        try {
          return JSON.parse(text);
        } catch (e) {
          throw new Error(`Expected JSON but got: ${text.substring(0, 200)}`);
        }
      });
    })
    .then(res => {
      // Check for errors (same as theme)
      if (res.status === 422) {
        throw new Error(res.description || 'Failed to add item to cart');
      }
      
      // Fetch updated cart (same as theme's addItemById)
      return fetch('/cart.js')
        .then(response => response.json())
        .then(cart => {
          button.innerHTML = 'Added!';
          button.classList.add('added');
          
          // Find the added item in cart
          const item = cart.items.find(item => item.variant_id === parseInt(variantId));
          
          // Trigger events exactly like theme does
          // First trigger quick-cart:updated to refresh drawer
          document.dispatchEvent(new CustomEvent('quick-cart:updated', { bubbles: true }));
          
          // Then trigger cart:updated with cart data
          document.dispatchEvent(new CustomEvent('cart:updated', {
            detail: { cart: cart },
            bubbles: true
          }));
          
          // Also manually refresh drawer to ensure it updates
          refreshCartDrawerContent().then(() => {
            // Update counter
            updateHeaderCartCounterDirect(cart);
          }).catch(() => {
            // Still update counter even if refresh fails
            updateHeaderCartCounterDirect(cart);
          });
          
          // Reset button after 1.5 seconds
          setTimeout(() => {
            button.innerHTML = originalText;
            button.disabled = false;
            button.classList.remove('added');
          }, 1500);
        });
    })
    .catch(error => {
      console.error('Error adding to cart (items format):', error);
      
      // Try old format as fallback (direct id and quantity)
      const cartAddUrl = '/cart/add.js';
      fetch(cartAddUrl, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id: parseInt(variantId),
          quantity: quantity
        })
      })
      .then(response => {
        if (!response.ok) {
          return response.text().then(text => {
            throw new Error(`HTTP ${response.status}: ${text.substring(0, 200)}`);
          });
        }
        
        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
          return response.json();
        }
        
        return response.text().then(text => {
          try {
            return JSON.parse(text);
          } catch (e) {
            throw new Error(`Expected JSON but got: ${text.substring(0, 200)}`);
          }
        });
      })
      .then(res => {
        if (res.status === 422) {
          throw new Error(res.description || 'Failed to add item to cart');
        }
        
        // Fetch updated cart
        return fetch('/cart.js')
          .then(response => response.json())
          .then(cart => {
            button.innerHTML = 'Added!';
            button.classList.add('added');
            
            // Trigger events
            document.dispatchEvent(new CustomEvent('quick-cart:updated', { bubbles: true }));
            document.dispatchEvent(new CustomEvent('cart:updated', {
              detail: { cart: cart },
              bubbles: true
            }));
            
            // Refresh drawer
            refreshCartDrawerContent().then(() => {
              updateHeaderCartCounterDirect(cart);
            }).catch(() => {
              updateHeaderCartCounterDirect(cart);
            });
            
            // Reset button
            setTimeout(() => {
              button.innerHTML = originalText;
              button.disabled = false;
              button.classList.remove('added');
            }, 1500);
          });
      })
      .catch(fallbackError => {
        console.error('Error adding to cart (old format):', fallbackError);
        button.innerHTML = 'Error - Try Again';
        button.disabled = false;
        
        setTimeout(() => {
          button.innerHTML = originalText;
        }, 2000);
      });
    });
  }


  /**
   * Update cart counter and drawer content (without opening drawer)
   * This matches the default theme behavior - manually refreshes cart drawer
   */
  function updateCartCounterAndDrawer() {
    // Step 1: Fetch updated cart data for counter
    fetch('/cart.js')
      .then(response => response.json())
      .then(cart => {
        // Update counter immediately
        updateHeaderCartCounterDirect(cart);
        
        // Step 2: Manually refresh cart drawer content
        // This ensures products appear in drawer without page refresh
        refreshCartDrawerContent().then(() => {
          // After refresh, trigger events so theme knows cart was updated
          document.dispatchEvent(new CustomEvent('cart:updated', {
            detail: { cart: cart },
            bubbles: true
          }));
          document.dispatchEvent(new CustomEvent('quick-cart:updated', {
            bubbles: true
          }));
        }).catch(error => {
          console.error('Error refreshing cart drawer:', error);
          // Still update counter even if drawer refresh fails
        });
      })
      .catch(error => {
        console.error('Error updating cart:', error);
      });
  }

  /**
   * Manually refresh the cart drawer content
   * This replicates what the theme's refreshQuickCart() does
   */
  function refreshCartDrawerContent() {
    return new Promise((resolve, reject) => {
      fetch('/?section_id=quick-cart')
        .then(response => response.text())
        .then(html => {
          const temp = document.createElement('div');
          temp.innerHTML = html;

          const newDrawer = temp.querySelector('.quick-cart__wrapper');
          const oldDrawer = document.querySelector('.quick-cart__wrapper');

          if (!newDrawer || !oldDrawer) {
            reject('Quick cart drawer not found');
            return;
          }

          oldDrawer.innerHTML = newDrawer.innerHTML;
          
          // Initialize free shipping bar after content is updated
          // This is needed because the free shipping bar requires JavaScript initialization
          initFreeShippingBar();
          
          // Initialize cross-sells if they exist
          initCrossSells();
          
          resolve();
        })
        .catch(reject);
    });
  }

  /**
   * Initialize free shipping bar after cart refresh
   */
  function initFreeShippingBar() {
    const freeShippingBarEl = document.querySelector('[data-free-shipping-bar]');
    if (!freeShippingBarEl) return;
    
    // Fetch current cart data to update free shipping bar
    fetch('/cart.js')
      .then(response => response.json())
      .then(cart => {
        updateFreeShippingBar(freeShippingBarEl, cart);
      })
      .catch(() => {
        // If cart fetch fails, use data from element
        updateFreeShippingBar(freeShippingBarEl, null);
      });
  }

  /**
   * Update free shipping bar display (replicates theme's functionality)
   */
  function updateFreeShippingBar(freeShippingBarEl, cart) {
    const threshold = parseInt(freeShippingBarEl.dataset.threshold) || 0;
    let cartTotal = cart ? cart.total_price : parseInt(freeShippingBarEl.dataset.cartTotal) || 0;
    
    // Account for currency rate (same as theme does)
    const currencyRate = (window.Shopify && window.Shopify.currency && window.Shopify.currency.rate) || 1;
    const adjustedThreshold = Math.round(threshold * currencyRate);
    const thresholdInCents = adjustedThreshold * 100;
    
    const successMessage = freeShippingBarEl.dataset.freeShippingSuccessMessage || 'Your order qualifies for free shipping!';
    const pendingMessage = freeShippingBarEl.dataset.freeShippingPendingMessage || 'Add {{ remaining_amount }} to qualify for free shipping';
    
    const messageEl = freeShippingBarEl.querySelector('.free-shipping-bar__message');
    const progressBar = freeShippingBarEl.querySelector('.free-shipping-bar__bar-progress');
    const progressComplete = freeShippingBarEl.querySelector('.free-shipping-bar__progress-complete');
    
    if (messageEl) {
      if (cartTotal >= thresholdInCents) {
        // Free shipping achieved
        messageEl.textContent = successMessage;
        freeShippingBarEl.dataset.isOverThreshold = 'true';
        
        if (progressBar) {
          progressBar.style.setProperty('--progress-width', '100%');
          progressBar.style.width = '100%';
        }
        if (progressComplete) {
          progressComplete.style.display = 'block';
        }
      } else {
        // Free shipping not yet achieved
        const remainder = Math.abs(cartTotal - thresholdInCents);
        freeShippingBarEl.dataset.isOverThreshold = 'false';
        
        const formattedRemainder = formatMoney(remainder);
        const message = pendingMessage.replace('{{ remaining_amount }}', '<span class="fs-body-bold">' + formattedRemainder + '</span>');
        messageEl.innerHTML = message;
        
        const progress = cartTotal < thresholdInCents ? (cartTotal / thresholdInCents) * 100 : 100;
        if (progressBar) {
          progressBar.style.setProperty('--progress-width', progress + '%');
          progressBar.style.width = progress + '%';
        }
        if (progressComplete) {
          progressComplete.style.display = 'none';
        }
      }
    }
    
    // Update cart total in dataset
    freeShippingBarEl.dataset.cartTotal = cartTotal;
    
    // Mark as loaded
    freeShippingBarEl.dataset.isLoaded = 'true';
  }

  /**
   * Initialize cross-sells after cart refresh
   */
  function initCrossSells() {
    // Cross-sells are usually initialized by the theme automatically
    // But we can trigger a custom event if needed
    const crossSellsEl = document.querySelector('[data-cross-sells]');
    if (crossSellsEl) {
      // Cross-sells initialization is typically handled by theme's CrossSells component
      // We'll just ensure the element is visible
      crossSellsEl.style.display = '';
    }
  }

  /**
   * Update header cart counter directly (fallback)
   */
  function updateHeaderCartCounterDirect(cart) {
    const itemCount = cart.item_count || 0;
    
    // Update data-cart-count attribute on the cart link (preserve icon structure)
    const cartLinks = document.querySelectorAll('[data-header-cart], .header__cart-link');
    cartLinks.forEach(link => {
      if (link.dataset) {
        link.dataset.cartCount = itemCount;
      }
      
      // Handle badge for regular cart link (icon only)
      if (link.classList.contains('header__icon-touch--cart')) {
        let countBadge = link.querySelector('.header__cart-link-count');
        
        if (itemCount > 0) {
          // Show badge with count
          if (!countBadge) {
            // Create badge if it doesn't exist - insert after the icon button
            countBadge = document.createElement('span');
            countBadge.className = 'header__cart-link-count';
            // Insert after icon button (or at end if no icon found)
            const iconButton = link.querySelector('.icon-button');
            if (iconButton) {
              iconButton.parentNode.insertBefore(countBadge, iconButton.nextSibling);
            } else {
              link.appendChild(countBadge);
            }
          }
          countBadge.textContent = itemCount;
          countBadge.style.display = ''; // Ensure visible
          countBadge.style.visibility = 'visible';
          countBadge.style.opacity = '1';
        } else {
          // Hide badge when cart is empty
          if (countBadge) {
            countBadge.textContent = '';
            countBadge.style.display = 'none';
          }
        }
      }
      
      // Handle badge for cart link with label (icon + label)
      if (link.classList.contains('header__icon-with-label-touch--cart')) {
        const iconWrapper = link.querySelector('.header__icon-with-label-touch-icon');
        if (iconWrapper) {
          let countBadge = iconWrapper.querySelector('.header__cart-link-count');
          
          if (itemCount > 0) {
            if (!countBadge) {
              // Create badge inside icon wrapper
              countBadge = document.createElement('span');
              countBadge.className = 'header__cart-link-count';
              iconWrapper.appendChild(countBadge);
            }
            countBadge.textContent = itemCount;
            countBadge.style.display = '';
            countBadge.style.visibility = 'visible';
            countBadge.style.opacity = '1';
          } else {
            if (countBadge) {
              countBadge.textContent = '';
              countBadge.style.display = 'none';
            }
          }
        }
        
        // Update label data attribute
        const label = link.querySelector('.header__icon-with-label-touch-label');
        if (label && label.dataset) {
          label.dataset.cartCount = itemCount;
        }
      }
    });
  }


  /**
   * Open cart drawer
   */
  function openCartDrawer() {
    setTimeout(() => {
      // Method 1: Try clicking the cart icon (most reliable - triggers theme's handler)
      const cartTrigger = document.querySelector('[data-header-cart], .header__cart-link, a[href*="/cart"]');
      if (cartTrigger) {
        cartTrigger.click();
        return;
      }
      
      // Method 2: Try theme's internal event system
      try {
        // The theme uses an event emitter system
        // Try to find and trigger quick-cart:open event
        if (window.theme && typeof window.theme.emit === 'function') {
          window.theme.emit('quick-cart:open');
        } else if (window.theme && window.theme.events && typeof window.theme.events.emit === 'function') {
          window.theme.events.emit('quick-cart:open');
        } else {
          // Method 3: Direct DOM manipulation
          const quickCartWrapper = document.querySelector('.quick-cart__wrapper');
          if (quickCartWrapper) {
            quickCartWrapper.classList.add('active');
            document.body.style.overflow = 'hidden';
          } else {
            // Last resort: try clicking any cart link
            const anyCartLink = document.querySelector('a[href*="/cart"]');
            if (anyCartLink) {
              anyCartLink.click();
            }
          }
        }
      } catch (error) {
        console.error('Error opening cart drawer:', error);
        // Last resort: try clicking any cart link
        const anyCartLink = document.querySelector('a[href*="/cart"]');
        if (anyCartLink) {
          anyCartLink.click();
        }
      }
    }, 150);
  }

  /**
   * Close modal
   */
  function closeModal() {
    const modal = document.getElementById(CONFIG.modalId);
    if (modal) {
      modal.style.display = 'none';
      document.body.style.overflow = '';
    }
  }

  /**
   * Format money - uses Shopify's money format (supports GBP, EUR, USD, etc.)
   */
  function formatMoney(cents) {
    // Use Shopify's formatMoney if available (handles currency automatically)
    if (typeof window.Shopify !== 'undefined' && window.Shopify.formatMoney) {
      // Get money format from theme settings or use default
      const moneyFormat = window.Shopify.money_format || window.theme?.moneyFormat || '{{amount}}';
      return window.Shopify.formatMoney(cents, moneyFormat);
    }
    
    // Fallback: try to use theme's formatMoney function
    if (typeof window.theme !== 'undefined' && typeof window.theme.formatMoney === 'function') {
      return window.theme.formatMoney(cents);
    }
    
    // Last resort: simple formatting
    const amount = (cents / 100).toFixed(2).replace(/\.00$/, '');
    
    // Try to get currency symbol from Shopify
    if (typeof window.Shopify !== 'undefined' && window.Shopify.currency) {
      const currency = window.Shopify.currency.active || window.Shopify.currency.shop || 'GBP';
      // Common currency symbols
      const symbols = {
        'GBP': '£',
        'USD': '$',
        'EUR': '€',
        'CAD': 'C$',
        'AUD': 'A$'
      };
      const symbol = symbols[currency] || currency;
      return symbol + amount;
    }
    
    // Default to GBP
    return '£' + amount;
  }

  /**
   * Escape HTML
   */
  function escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();

